import { useState, useCallback } from 'react';

const AUTH_API = '/api/portal/auth';

export interface AuthUser {
  username: string;
  name: string;
  team_id: string;
}

export interface ManagerInfo {
  name: string;
  telegram: string;
  email: string;
  avatar: string | null;
}

const AUTH_KEY = 'portal_auth_user';
const MANAGER_KEY = 'portal_manager';

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function getStoredManager(): ManagerInfo | null {
  try {
    const raw = localStorage.getItem(MANAGER_KEY);
    return raw ? (JSON.parse(raw) as ManagerInfo) : null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);

  const login = useCallback(async (loginVal: string, password: string): Promise<boolean> => {
    const res = await fetch(AUTH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: loginVal.trim(), password }),
    });

    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.ok || !data.user) return false;

    const authUser: AuthUser = {
      username: data.user.username,
      name: data.user.full_name,
      team_id: data.user.team_id,
    };
    localStorage.setItem(AUTH_KEY, JSON.stringify(authUser));

    const m = data.user.manager;
    if (m) {
      const manager: ManagerInfo = {
        name: m.name,
        telegram: m.telegram,
        email: m.email,
        avatar: m.avatar ?? null,
      };
      localStorage.setItem(MANAGER_KEY, JSON.stringify(manager));
    } else {
      localStorage.removeItem(MANAGER_KEY);
    }

    setUser(authUser);
    return true;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(MANAGER_KEY);
    setUser(null);
  }, []);

  return { user, login, logout };
}
